/* A row that scrolls, and fetches.

   The markup already scrolls and snaps on its own — a phone needs nothing
   from here. What this adds is the pair of arrows for a screen without touch,
   and the batches after the first: the example gardens on the home page do
   not come with everything they could show.

   Nothing is fetched until the visitor asks to see further, and then it is
   one batch, not the rest. The answer is html, from the same partial the page
   rendered its first batch with — see the note at the top of
   _example_slides.php for why it is not json with a template on this side.

   Markup contract — the track is a <ul> that scrolls, carrying the address to
   ask for the next batch. The arrows are the caller's and may be left out.

     <ul data-x data-endpoint="/" class="flex overflow-x-auto snap-x …">
       <li>…</li>
       <li data-next="24">…</li>
     </ul>

   How the children are laid out is the page's business. All this reads is
   where each one sits.

   The swipe hint is the caller's too and may be left out. It is rendered
   `hidden` and shown from here, because whether it belongs depends on things
   the markup cannot see — see showHint().

   ── the hand or the arrows, never both ──
   Which of the two a row carries is decided by the pointer and not by the
   width of the screen. A finger gets the hand: it can swipe, and an arrow
   beside a row that already moves under it is a button in the way. Anything
   else gets the arrows, at every width — a browser in mobile view is a mouse
   on a narrow screen, and it cannot swipe, cannot drag a scroll container
   whose bar `no-scrollbar` hides, and was left with a row that answered to
   nothing (client, 27 Aug 2026).

   The arrows are hidden in css, by `not-pointer-coarse:` on the markup, and
   the hand is hidden here by the query below. `(pointer: coarse)` and its
   negation are the two halves of one switch, so the pair is never both on
   screen or both off it — keep the two in step if either ever moves.

   Where it is in the list is not counted here: the last child of a batch says
   what to ask for after it, and a batch whose last child says nothing was the
   last one. So the page decides what a batch is — a page number for the
   examples — and this only ever hands it back. */
export const createSlider = ({ track, prev, next, hint, param }) => {
  if (!track) return;

  const arrow = next?.querySelector('[data-arrow]');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const endpoint = track.dataset.endpoint || '/';

  let busy = false;

  const cursor = () => track.lastElementChild?.dataset.next || '';

  const more = () => !!cursor();

  /* ── where the cards sit ──
     A screenful is not a whole number of cards: three of them and two gaps
     add up to the width of the track, so the fourth card starts a gap further
     along than that. Scrolling by the width of the track therefore lands 24
     pixels short every time, and by the second press the row was showing two
     cards and two halves.

     So the arrows move from card to card and not by a distance — every place
     the row can come to rest, left to right, measured off the first child.
     `offsetLeft` is against whichever ancestor is positioned rather than
     against the track, so the first child is the ruler and the rest are read
     off it.

     A place is not the same thing as a child: the categories stack two rows
     of tiles into one column of the track from `md` up, and two children that
     share a column share an offset. Hence the de-duplicating — what the
     arrows count in is columns, whatever a column happens to hold. */
  const stops = () => {
    const base = track.children[0]?.offsetLeft || 0;
    const out = [];

    for (const child of track.children) {
      const x = child.offsetLeft - base;

      if (!out.length || x - out[out.length - 1] > 1) out.push(x);
    }

    return out.length ? out : [0];
  };

  /* One screenful, for deciding when to fetch rather than where to scroll. */
  const step = () => track.clientWidth;

  /* What is still to the right of the viewport. `scrollLeft` is fractional on
     a zoomed or hidpi screen, so nothing here compares for equality. */
  const ahead = () => track.scrollWidth - track.scrollLeft - track.clientWidth;

  const paint = () => {
    if (prev) prev.disabled = track.scrollLeft <= 4;
    if (next) next.disabled = ahead() <= 4 && !more();
  };

  /* ── the next batch ──
     Failure is silent and leaves everything as it was: the row still holds
     what it holds, and the next press tries again. */
  const fetchBatch = async () => {
    if (busy || !more()) return;

    busy = true;
    next?.setAttribute('aria-busy', 'true');
    arrow?.classList.add('animate-load-spin');

    const asked = cursor();

    try {
      // The endpoint may already carry a query of its own.
      const join = endpoint.includes('?') ? '&' : '?';

      const response = await fetch(`${endpoint}${join}${param}=${encodeURIComponent(asked)}`, {
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(response.status);

      /* Bare <li>s: a parser throws them away unless they are handed a list
         to sit in. */
      const batch = new DOMParser()
        .parseFromString(`<ul>${await response.text()}</ul>`, 'text/html')
        .querySelector('ul');

      if (batch?.children.length) {
        /* The cursor moves to the last child of what arrives; the one that
           carried us here is not the end of the row any more. */
        const previous = track.lastElementChild;

        /* Snapping off for the length of the append. The row snaps
           mandatorily, and a mandatory container that is handed new children
           re-snaps — which threw the row a whole card further along and skipped
           the one the arrow had just brought into view. With snapping off
           there is nothing to re-snap; the css takes over again a tick later,
           by which time the scrolling has somewhere to settle. */
        const keep = track.scrollLeft;

        track.style.scrollSnapType = 'none';
        track.append(...batch.children);

        /* Only if it did move after all — assigning it cancels whatever
           smooth scroll is running. */
        if (Math.abs(track.scrollLeft - keep) > 1) track.scrollLeft = keep;

        setTimeout(() => {
          track.style.scrollSnapType = '';
        });

        delete previous.dataset.next;
      } else {
        /* Nothing came back, whatever the cursor promised: this is the end of
           the row, and taking the cursor off says so. */
        delete track.lastElementChild.dataset.next;
      }
    } catch {
      /* The cursor is untouched, so the same batch is asked for again. */
    } finally {
      busy = false;
      next?.removeAttribute('aria-busy');
      arrow?.classList.remove('animate-load-spin');
      paint();
    }
  };

  /* Fetch when there is less than a screenful left to scroll into. Called
     from the arrow and from the scrolling itself, so a finger on a phone
     brings in the next batch exactly as an arrow does. */
  const topUp = () => {
    if (ahead() < step() * 1.2) fetchBatch();
  };

  const go = async (direction) => {
    /* Forward with nothing loaded to move into: wait for the batch, then
       scroll into it. Anywhere else the fetch runs behind the scrolling and
       is not waited for. */
    if (direction > 0 && ahead() < step() * 0.5) {
      await fetchBatch();
    } else {
      topUp();
    }

    /* Read after the fetch: a batch that has just landed adds columns, and
       the arrow is allowed to move into them. */
    const columns = stops();

    /* One column to the next, its gap included, and so how many stand side by
       side and how far one press goes. */
    const pitch = columns.length > 1 ? columns[1] - columns[0] : track.clientWidth;
    const perView = Math.max(1, Math.round(track.clientWidth / pitch));

    /* Whichever column the row is nearest to, rather than which one it was
       last sent to: a finger may have left it anywhere. */
    const first = columns.reduce(
      (best, x, i) =>
        Math.abs(x - track.scrollLeft) < Math.abs(columns[best] - track.scrollLeft) ? i : best,
      0
    );

    const target = Math.min(Math.max(first + direction * perView, 0), columns.length - 1);

    track.scrollTo({ left: columns[target], behavior: reduce ? 'auto' : 'smooth' });
  };

  /* ── the swipe hint ──
     Rendered `hidden` beside the heading and shown from here, because whether
     it belongs is not something the markup knows: a row with everything
     already in view has nothing to hint at, and a visitor with a mouse has
     the arrows.

     `(pointer: coarse)` and not a breakpoint. What the hint says is "put your
     finger on this", and that is about the pointer the visitor has, not about
     how wide their screen is. It is the exact negation of the
     `not-pointer-coarse:` the arrows are shown by, so a row carries one of
     the two and never neither.

     It leaves at the first touch of the row and not before — the sweep runs
     until then (client, 25 Aug 2026) — and it never comes back. Once the
     visitor has swiped, the row has said what the hand was there to say. */
  const showHint = () => {
    if (!hint || reduce || !matchMedia('(pointer: coarse)').matches || ahead() < 8) return;

    const reveal = () => {
      hint.classList.remove('hidden');

      track.addEventListener('scroll', () => hint.remove(), { once: true, passive: true });
    };

    /* Not before the row is on the screen. The example gardens are most of a
       page down; a hint that swept its three times while the visitor was
       still reading the banner has told nobody anything. */
    const watcher = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        watcher.disconnect();
        reveal();
      },
      { threshold: 0.4 }
    );

    watcher.observe(track);
  };

  /* The arrows were rendered disabled, for the browser that never gets here.
     From now on it is the position in the row that says whether they work. */
  prev?.removeAttribute('disabled');
  next?.removeAttribute('disabled');

  prev?.addEventListener('click', () => go(-1));
  next?.addEventListener('click', () => go(1));

  track.addEventListener(
    'scroll',
    () => {
      paint();
      topUp();
    },
    { passive: true }
  );

  /* The step is the width of the track, so a rotated phone or a dragged
     window changes what the arrows are worth. */
  addEventListener('resize', paint, { passive: true });

  paint();
  showHint();
};
